import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useMinuteNow } from '@hooks/useMinuteNow';
import { formatTime } from '@lib/datetime';
import { PHONE_STOPS_VIEW, PHONE_STOPS_VIEW_PARAM, ROUTES } from '@lib/routes';
import { useDeliveryFlow } from '../hooks/useDeliveryFlow';
import { useRouteEta } from '../hooks/useRouteEta';
import { minutesSinceLastSeen, useRouteMonitoringJoin } from '../hooks/useRouteMonitoringJoin';
import { useRoutePlanningStops } from '../hooks/useRoutePlanningStops';
import { enRouteTimes } from '../panes/enRouteModel';
import { buildDriverStops, rowJobCount } from './driverStopsModel';
import type { RoutePlanningStop } from '../map/RoutePlanningMap';
import { PhoneDriverMap } from './PhoneDriverMap';
import { StopLine } from './StopLine';

const DASH = '—';
// One empty answer for every render, so a missing stops read keeps one identity.
const NO_STOPS: RoutePlanningStop[] = [];

interface PhoneDriverStopsProps {
  /** The Onfleet worker id: the ETA report knows drivers by it alone. */
  workerId: string;
  today: string;
}

/**
 * One driver's delivery jobs on a phone (#2677): the ones still to deliver by
 * ETA, the late ones flagged under a banner, the delivered ones folded away.
 * The reads are the desktop's (same hooks, same query keys) and the join is
 * the desktop route map's. The order is the ETA order, not the route the
 * driver runs, so no row carries a number.
 */
export function PhoneDriverStops({ workerId, today }: PhoneDriverStopsProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const onMap = searchParams.get(PHONE_STOPS_VIEW_PARAM) === PHONE_STOPS_VIEW.map;
  // Replace, not push: flipping between list and map leaves no history trail.
  const showMap = (map: boolean) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (map) next.set(PHONE_STOPS_VIEW_PARAM, PHONE_STOPS_VIEW.map);
        else next.delete(PHONE_STOPS_VIEW_PARAM);
        return next;
      },
      { replace: true },
    );
  const nowMs = useMinuteNow();
  const eta = useRouteEta(true);
  const stops = useRoutePlanningStops(today);
  const flow = useDeliveryFlow();
  const times = flow.data ? enRouteTimes(flow.data, today) : null;
  const join = useRouteMonitoringJoin({
    stops: stops.data?.stops ?? NO_STOPS,
    etaDrivers: eta.data?.date === today ? eta.data.drivers : undefined,
    isToday: true,
  });
  const driver = join.drivers.find((d) => d.workerId === workerId);

  let body: ReactNode;
  // A failed background refetch keeps the last answer on screen.
  if (eta.isError && eta.data === undefined) {
    body = (
      <div className="flex flex-col items-start gap-2 rounded-xl bg-card p-3.5">
        <span className="text-destructive">{t('dispatch.phone.stops.loadFailed')}</span>
        <button
          type="button"
          onClick={() => void eta.refetch()}
          className="inline-flex h-11 items-center rounded-lg border border-border bg-card px-3.5 font-medium text-foreground"
        >
          {t('ui.retry')}
        </button>
      </div>
    );
  } else if (eta.isPending || stops.isPending || flow.isPending) {
    body = (
      <div aria-busy="true" className="flex flex-col gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-11 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  } else if (!driver) {
    body = <p className="m-0 text-muted-foreground">{t('dispatch.phone.stops.notFound')}</p>;
  } else if (onMap) {
    body = (
      <PhoneDriverMap
        workerId={workerId}
        stops={stops.data?.stops ?? NO_STOPS}
        monitoringJoin={join}
        rows={buildDriverStops(driver, times?.windowEndMs ?? null)}
      />
    );
  } else {
    const { pending, done, lateCount } = buildDriverStops(driver, times?.windowEndMs ?? null);
    const lastDone = done.reduce<number | null>(
      (latest, row) =>
        row.completedAt !== null && (latest === null || row.completedAt > latest)
          ? row.completedAt
          : latest,
      null,
    );
    const doneJobs = done.reduce((sum, row) => sum + rowJobCount(row), 0);
    body = (
      <>
        {lateCount > 0 && (
          <div className="rounded-xl bg-warning/12 px-3.5 py-2.5 font-medium">
            {t('dispatch.phone.stops.lateBanner', {
              count: lateCount,
              time: times?.windowEnd ?? DASH,
            })}
          </div>
        )}
        {pending.length > 0 && (
          <div className="rounded-xl bg-card outline outline-1 -outline-offset-1 outline-border/50">
            {pending.map((row) => (
              <StopLine key={row.taskId} row={row} nowMs={nowMs} />
            ))}
          </div>
        )}
        {done.length > 0 && (
          <details className="rounded-xl bg-card outline outline-1 -outline-offset-1 outline-border/50">
            <summary className="flex min-h-11 cursor-pointer items-center px-3.5 text-muted-foreground">
              {t('dispatch.phone.stops.doneSummary', {
                count: doneJobs,
                time: lastDone === null ? DASH : formatTime(lastDone),
              })}
            </summary>
            {done.map((row) => (
              <StopLine key={row.taskId} row={row} nowMs={nowMs} />
            ))}
          </details>
        )}
      </>
    );
  }

  const lastSeen =
    driver && !driver.onDuty
      ? t('routePlanning.driver.offDuty')
      : driver && driver.timeLastSeen !== null
        ? t('routePlanning.driver.lastSeen', {
            minutes: minutesSinceLastSeen(driver.timeLastSeen, nowMs),
          })
        : null;

  return (
    <div data-testid="phone-driver-stops" className="flex flex-col gap-3">
      <div className="flex min-h-11 items-center gap-2">
        <Link
          to={ROUTES.dispatch.root}
          aria-label={t('dispatch.phone.stops.back')}
          className="-ml-2 inline-flex size-11 items-center justify-center rounded-lg text-foreground"
        >
          <ChevronLeft aria-hidden className="size-5" />
        </Link>
        <div className="flex min-w-0 flex-col">
          <h2 className="m-0 truncate text-[18px] leading-[1.3] font-semibold">
            {driver?.workerName ?? DASH}
          </h2>
          {lastSeen && <span className="text-[12.5px] text-muted-foreground">{lastSeen}</span>}
        </div>
      </div>
      <div
        role="group"
        aria-label={t('dispatch.phone.stopsView.label')}
        className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1"
      >
        {[false, true].map((map) => (
          <button
            key={String(map)}
            type="button"
            aria-pressed={onMap === map}
            onClick={() => showMap(map)}
            className={`h-11 rounded-md font-medium ${onMap === map ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
          >
            {t(map ? 'dispatch.phone.stopsView.map' : 'dispatch.phone.stopsView.list')}
          </button>
        ))}
      </div>
      {body}
    </div>
  );
}
